import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from "classnames";

import { useSpring, useTransition, animated } from "@react-spring/web";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { useSelector } from "react-redux";

import { isSimulationVideoFormat } from "src/constants/simulation";
import { LearningRecordSimulation } from "src/types/coaching/learners";
import WaveSurfer from "wavesurfer.js";

import { SoundVisualizer } from "../visualiser";
import Tooltip from "src/components/tooltip";

import styles from "./player.module.scss";
import { SimulationContext } from "../../context";

interface CallRecordingPlayerProps {
  simulation: LearningRecordSimulation;
  minimized: boolean;
  onPlay?: () => void;
}

export const CallRecordingPlayer: React.FC<CallRecordingPlayerProps> = ({ simulation, minimized, onPlay }) => {
  const simulationContext = React.useContext(SimulationContext);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<WaveSurfer | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const user = useSelector((state: any) => _.get(state, "users.me", {}));
  const userAvatar = _.get(user, "icon.full", "");
  const initials = `${(_.get(user, "firstName", "")[0] || "")}${(_.get(user, "lastName", "")[0] || "")}`.toUpperCase();

  const getMediaType = (url: string): 'video' | 'audio' => {
    return url.endsWith('.mp4') ? 'video' : 'audio';
  };

  const { url, mediaType } = useMemo(() => {
    const url = _.get(simulation, 'report.callRecordingUrl') as string | undefined;
    const mediaType = url ? getMediaType(url) : null;
    return { url, mediaType };
  }, [simulation]);

  const [playerState, setPlayerState] = useState({
    muted: false,
    playbackRate: 1,
    volume: 100,
    duration: 0,
    currentTime: 0,
    waveformLoaded: false,
    audioPlaying: false,
  });

  const loadingTransition = useTransition(!playerState.waveformLoaded, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const [playerStyles] = useSpring({
    transform: playerState.waveformLoaded ? "translateY(0%)" : "translateY(100%)",
    opacity: playerState.waveformLoaded ? 1 : 0,
  }, [playerState.waveformLoaded]);

  const handleVolumeChange = useCallback(
    (volume: number) => {
      if (mediaType === "video" && videoRef.current) {
        videoRef.current.volume = volume / 100;
        setPlayerState((state) => ({
          ...state,
          volume,
        }));
        return;
      }
      if (playerRef.current) {
        playerRef.current.setVolume(volume / 100);
        setPlayerState((state) => ({
          ...state,
          volume,
        }));
      }
    },
    [mediaType],
  );

  const initAudioWaveSurfer = useCallback((loadUrl: string) => {
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {
        console.error(e);
      }
      playerRef.current = null;
    }

    if (!containerRef.current) {
      return;
    }

    playerRef.current = WaveSurfer.create({
      container: containerRef.current,
      height: 14,
      waveColor: "#fff",
      progressColor: "#fff",
      barWidth: 3,
      barGap: 2,
      barRadius: 16,
      cursorWidth: 0,
      normalize: true,
      hideScrollbar: true,
    });
    playerRef.current.load(loadUrl);

    playerRef.current.on("ready", () => {
      setPlayerState((s) => ({
        ...s,
        duration: Math.round(playerRef.current?.getDuration() || 0),
        waveformLoaded: true,
      }));
      playerRef.current?.setTime(0);
    });
    playerRef.current.on("play", () =>
      setPlayerState((s) => ({ ...s, audioPlaying: playerRef.current?.isPlaying() || false })),
    );
    playerRef.current.on("pause", () =>
      setPlayerState((s) => ({ ...s, audioPlaying: playerRef.current?.isPlaying() || false })),
    );
    playerRef.current.on("audioprocess", () =>
      setPlayerState((s) => ({
        ...s,
        currentTime: Math.round(playerRef.current?.getCurrentTime() || 0),
      })),
    );
    playerRef.current.on("seeking", () =>
      setPlayerState((s) => ({
        ...s,
        currentTime: playerRef.current?.getCurrentTime() || 0,
      })),
    );
    playerRef.current.on("finish", () =>
      setPlayerState((s) => ({ ...s, audioPlaying: false, currentTime: 0 })),
    );
    playerRef.current.on("error", (e) => {
      console.error("WaveSurfer error:", e);
      setPlayerState((s) => ({ ...s, waveformLoaded: true }));
    });
  }, []);

  const initVideoWaveform = useCallback((loadUrl: string, isCancelled: () => boolean) => {
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {
        console.error(e);
      }
      playerRef.current = null;
    }

    if (!containerRef.current) {
      return;
    }

    playerRef.current = WaveSurfer.create({
      container: containerRef.current,
      height: 14,
      waveColor: "#fff",
      progressColor: "#fff",
      barWidth: 3,
      barGap: 2,
      barRadius: 16,
      cursorWidth: 0,
      normalize: true,
      hideScrollbar: true,
    });
    playerRef.current.load(loadUrl);

    playerRef.current.on("ready", () => {
      if (isCancelled()) {
        return;
      }
      const hidden = playerRef.current?.getMediaElement();
      if (hidden) {
        hidden.muted = true;
        hidden.volume = 0;
        hidden.pause();
      }
      setPlayerState((s) => {
        const v = videoRef.current;
        if (v) {
          v.volume = s.volume / 100;
          v.playbackRate = s.playbackRate;
        }
        return {
          ...s,
          duration: Math.round(playerRef.current?.getDuration() || 0),
          waveformLoaded: true,
        };
      });
      playerRef.current?.setTime(0);
    });

    playerRef.current.on("interaction", (newTime: number) => {
      const v = videoRef.current;
      if (v && Number.isFinite(newTime)) {
        v.currentTime = newTime;
        playerRef.current?.setTime(newTime);
      }
    });

    playerRef.current.on("error", (e) => {
      console.error("WaveSurfer error:", e);
      setPlayerState((s) => ({ ...s, waveformLoaded: true }));
    });
  }, []);

  useEffect(() => {
    setPlayerState((s) => ({
      ...s,
      waveformLoaded: false,
      duration: 0,
      currentTime: 0,
      audioPlaying: false,
    }));
  }, [url, mediaType]);

  useEffect(() => {
    if (mediaType !== "video" || !url) {
      return;
    }
    const t = window.setTimeout(() => {
      setPlayerState((s) => (s.waveformLoaded ? s : { ...s, waveformLoaded: true }));
    }, 60000);
    return () => window.clearTimeout(t);
  }, [url, mediaType]);

  useEffect(() => {
    if (!url || mediaType !== "audio" || !WaveSurfer) {
      return;
    }
    initAudioWaveSurfer(url);
    return () => {
      try {
        playerRef.current?.destroy();
      } catch (e) {
        console.error(e);
      }
      playerRef.current = null;
    };
  }, [url, mediaType, initAudioWaveSurfer]);

  useLayoutEffect(() => {
    if (!url || mediaType !== "video" || !WaveSurfer) {
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryInit = () => {
      if (cancelled) {
        return;
      }
      if (containerRef.current) {
        initVideoWaveform(url, () => cancelled);
        return;
      }
      if (attempts < 40) {
        attempts += 1;
        requestAnimationFrame(tryInit);
      }
    };

    tryInit();

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch (e) {
        console.error(e);
      }
      playerRef.current = null;
    };
  }, [url, mediaType, initVideoWaveform]);

  useEffect(() => {
    if (mediaType !== "video" || !playerState.waveformLoaded) {
      return;
    }
    const v = videoRef.current;
    if (!v) {
      return;
    }
    let lastRounded = -1;
    const onTime = () => {
      const t = v.currentTime;
      playerRef.current?.setTime(t);
      const rounded = Math.round(t);
      if (rounded !== lastRounded) {
        lastRounded = rounded;
        setPlayerState((s) => ({
          ...s,
          currentTime: rounded,
        }));
      }
    };
    const onPlay = () => setPlayerState((s) => ({ ...s, audioPlaying: true }));
    const onPause = () => setPlayerState((s) => ({ ...s, audioPlaying: false }));
    const onEnded = () => setPlayerState((s) => ({ ...s, audioPlaying: false, currentTime: 0 }));
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
    };
  }, [mediaType, playerState.waveformLoaded, url]);

  useEffect(() => {
    return () => {
      try {
        playerRef.current?.destroy();
      } catch (e) {
        console.error(e);
      }
      playerRef.current = null;
    };
  }, [url, mediaType]);

  useEffect(() => {
    if (playerState.audioPlaying && onPlay) {
      onPlay();
    }
  }, [playerState.audioPlaying, onPlay]);

  const onPlayPause = useCallback(() => {
    if (mediaType === "video" && videoRef.current) {
      const v = videoRef.current;
      if (v.paused) {
        void v.play();
      } else {
        v.pause();
      }
      return;
    }
    playerRef.current?.playPause();
  }, [mediaType]);

  const handleSkipBackward = useCallback(() => {
    if (mediaType === "video" && videoRef.current) {
      const v = videoRef.current;
      const t = Math.max(v.currentTime - 15, 0);
      v.currentTime = t;
      playerRef.current?.setTime(t);
      return;
    }
    if (playerRef.current) {
      const t = Math.max(playerRef.current.getCurrentTime() - 15, 0);
      playerRef.current.seekTo(t / playerRef.current.getDuration());
    }
  }, [mediaType]);

  const handleSkipForward = useCallback(() => {
    if (mediaType === "video" && videoRef.current) {
      const v = videoRef.current;
      const dur = v.duration || playerRef.current?.getDuration() || 0;
      const t = Math.min(v.currentTime + 10, dur);
      v.currentTime = t;
      playerRef.current?.setTime(t);
      return;
    }
    if (playerRef.current) {
      const dur = playerRef.current.getDuration();
      const t = Math.min(playerRef.current.getCurrentTime() + 10, dur);
      playerRef.current.seekTo(t / dur);
    }
  }, [mediaType]);

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      if (mediaType === "video" && videoRef.current) {
        videoRef.current.playbackRate = rate;
        setPlayerState((state) => ({
          ...state,
          playbackRate: rate,
        }));
        return;
      }
      if (playerRef.current) {
        playerRef.current.setPlaybackRate(rate);
        setPlayerState((state) => ({
          ...state,
          playbackRate: rate,
        }));
      }
    },
    [mediaType],
  );

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const mm = String(mins).padStart(2, "0");
    const ss = String(secs).padStart(2, "0");
    return hrs === 0 ? `${mm}:${ss}` : `${hrs}:${mm}:${ss}`;
  };

  const handleCollapse = () => simulationContext.changeSecondaryView?.("call-recording-minimized");
  const handleExpand = () => simulationContext.changeSecondaryView?.("call-recording");
  const handleClose = () => simulationContext.changeSecondaryView?.(null);

  if (!url || !mediaType) {
    return null;
  }

  return (
    <div
      className={cx(styles.callRecordingPlayer, {
        [styles.minimized]: minimized,
        [styles.expandedInPanel]: !minimized,
      })}
    >
      <div className={styles.mediaContainer}>
        {loadingTransition((style, item) =>
          item ? (
            <animated.div className={styles.loading} style={style}>
              <AudioVisualizerLoader />
            </animated.div>
          ) : null,
        )}

        {minimized && (
          <>
            <div className={styles.rightActions}>
              <i className="paperflite-screen-expand" onClick={handleExpand} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && handleExpand()} />
              <i className="paperflite-close" onClick={handleClose} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && handleClose()} />
            </div>
            <div className={styles.centerActions}>
              <button type="button" onClick={handleSkipBackward} className={cx(styles.action, styles.rewind)}>
                <i className="paperflite-reload" />
                <span>15</span>
              </button>
              <button type="button" onClick={onPlayPause} className={cx(styles.action, styles.play)}>
                <i className={playerState.audioPlaying ? "paperflite-pause" : "paperflite-play"} />
              </button>
              <button type="button" onClick={handleSkipForward} className={cx(styles.action, styles.forward)}>
                <i className="paperflite-reload" />
                <span>10</span>
              </button>
            </div>
          </>
        )}

        <div className={styles.playerWrapper}>
          {mediaType === "video" && (
            <video
              ref={videoRef}
              src={url}
              playsInline
              preload="auto"
              className={cx(styles.videoPlayer, { [styles.minimized]: minimized })}
              onError={() => {
                setPlayerState((s) => ({ ...s, waveformLoaded: true }));
              }}
            />
          )}

          {mediaType === "audio" && (
            <div className={cx(styles.audioAvatar, { [styles.minimized]: minimized, [styles.noAvatar]: !userAvatar })}>
              {userAvatar && (
                <img src={userAvatar} className={styles.avatarImage} alt="" />
              )}
              {!userAvatar && (
                <div className={cx(styles.initialsAvatar, { [styles.minimized]: minimized })}>{initials}</div>
              )}
            </div>
          )}

          <div className={styles.playerContainer}>
            <animated.div className={styles.player} style={playerStyles}>
              <div className={styles.track}>
              {!minimized && (
                <>
                  <div className={styles.actions}>
                    <button type="button" onClick={handleSkipBackward} className={cx(styles.action, styles.rewind)}>
                      <i className="paperflite-reload" />
                      <span>15</span>
                    </button>
                    <button type="button" onClick={onPlayPause} className={cx(styles.action, styles.play)}>
                      <i className={playerState.audioPlaying ? "paperflite-pause" : "paperflite-play"} />
                    </button>
                    <button type="button" onClick={handleSkipForward} className={cx(styles.action, styles.forward)}>
                      <i className="paperflite-reload" />
                      <span>10</span>
                    </button>
                  </div>
                  <div className={styles.time}>{formatTime(playerState.currentTime || 0)}</div>
                </>
              )}

              <div ref={containerRef} className={styles.waveform} />

              {!minimized && (
                <>
                  <div className={styles.time}>{formatTime(playerState.duration || 0)}</div>
                  <div className={cx(styles.actions, styles.rightIcons)}>
                    <VolumeButton volume={playerState.volume} onChange={handleVolumeChange} />
                    <PlayRateButton playbackRate={playerState.playbackRate} onChange={handlePlaybackRateChange} />
                    <Tooltip content="Collapse player" placement="top">
                      <button type="button" onClick={handleCollapse} className={cx(styles.action, styles.collapse)}>
                        <i className="paperflite-screen-collapse" />
                      </button>
                    </Tooltip>
                  </div>
                </>
              )}
              </div>
            </animated.div>
          </div>
        </div>
      </div>
    </div>
  );
};


const AudioVisualizerLoader: React.FC = () => (
  <div className={styles.audioVisualizerLoader}>
    <SoundVisualizer
      disabled={false}
      volumeLevel={1}
      barCount={120}
      barWidth="2px"
      height={30}
      color="#FF45C2"
      durationInSeconds={0.25}
      cornerRadius={8}
      verticalAlignment="center"
    />
    <div className={styles.message}>Analyzing call recording...</div>
  </div>
);


interface VolumeButtonProps {
  volume: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: number) => void;
}

const VolumeButton: React.FC<VolumeButtonProps> = ({ volume, onChange }) => {
  const volumePopover = useMemo(() => {
    const handleVolumeChange = (e) => {
      if (onChange) {
        onChange(parseInt(e.target.value));
      }
    };

    return (
      <Popover id="volume-popup" className={styles.volumePopover}>
        <Popover.Body>
          <div className={styles.volumeControl}>
            <div className={styles.progress} style={{
              height: `calc(${volume}% + ${(volume > 25 ? 0 : 4)}px)`,
              borderTopLeftRadius: volume > 50 ? 4 : 0,
              borderTopRightRadius: volume > 50 ? 4 : 0,
            }} />
            <input type="range"
              min="0" max="100"
              value={volume}
              className={styles.volumeSlider}
              onChange={handleVolumeChange}
            />
          </div>
        </Popover.Body>
      </Popover>
    );

  }, [volume, onChange]);

  return (
    <OverlayTrigger trigger="click" placement="top" overlay={volumePopover} rootClose>
      <button className={cx(styles.action, styles.volume)}>
        <i className="paperflite-volume-on"></i>
      </button>
    </OverlayTrigger>
  );
}

interface PlayRateButtonProps {
  playbackRate: number;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: number) => void;
}

const PlayRateButton: React.FC<PlayRateButtonProps> = ({ playbackRate, onChange }) => {
  const playRatePopover = useMemo(() => {
    const handleRateChange = (rate: number) => {
      if (onChange) {
        onChange(rate);
      }
    };

    return (
      <Popover id="playrate-popup" className={styles.playRatePopover}>
        <Popover.Body>
          <div className={styles.options}>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 0.5 })}
              onClick={() => handleRateChange(0.5)}
            >
              <span className={styles.label}>0.5X</span>
              {playbackRate === 0.5 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 1 })}
              onClick={() => handleRateChange(1.0)}
            >
              <span className={styles.label}>1.0X</span>
              {playbackRate === 1 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 1.5 })}
              onClick={() => handleRateChange(1.5)}
            >
              <span className={styles.label}>1.5X</span>
              {playbackRate === 1.5 && <i className="paperflite-tick-selected"></i>}
            </div>
            <div
              className={cx(styles.option, { [styles.selected]: playbackRate === 2 })}
              onClick={() => handleRateChange(2.0)}
            >
              <span className={styles.label}>2.0X</span>
              {playbackRate === 2.0 && <i className="paperflite-tick-selected"></i>}
            </div>
          </div>
        </Popover.Body>
      </Popover>
    );

  }, [playbackRate, onChange]);

  return (
    <OverlayTrigger trigger="click" placement="top" overlay={playRatePopover} rootClose>
      <button className={cx(styles.action, styles.speed)}>
        <i className="paperflite-circle-dashed-play"></i>
      </button>
    </OverlayTrigger>
  );
}
